<template>
    <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors, handleSubmit }">
        <modal
            :id="modalId"
            title="Settings"
            ok-title="Save"
            :ok-disabled="loading || !meta.valid"
            @shown="setValues"
            @ok.prevent="handleSubmit(_, save)"
            @hide="hideModal"
            @hidden="hiddenModal"
        >
            <div class="d-flex flex-column">
                <b-row>
                    <b-col>
                        <label>Commission:</label>
                        <validation-provider
                            name="commission"
                            rules="required|validFloat"
                        >
                            <form-input
                                v-model="values.commission"
                                :rightIcon="['fas', 'percent']"
                                :error="errors.commission"
                                @input="(val) => setFieldValue('commission', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <div class="mb-3">
                    <span>Enable Feed to VRBO with:</span>
                    <div class="mt-2">
                        <div class="custom-tooltip pb-2">
                            <b-form-radio
                                v-model="values.instant_booking"
                                name="instant_booking"
                                :value="true"
                                v-b-tooltip.hover="{ container: 'vue-app', variant: 'info' }"
                                title="Get more bookings!"
                            >
                                Instant Booking
                            </b-form-radio>
                        </div>

                        <b-form-radio
                            v-model="values.instant_booking"
                            name="feed_enabled"
                            :value="false"
                            class="mt-md-0 mt-3"
                        >
                            Online Booking with 24 hours review
                        </b-form-radio>
                    </div>
                </div>
                <validation-provider
                    name="rate_rules_export_period"
                    rules="required"
                >
                    <form-select
                        label="Rate/Rules export period"
                        :options="rateRulesExportPeriodOptions"
                        v-model="values.rate_rules_export_period"
                        :error="errors.rate_rules_export_period"
                        @change="(val) => setFieldValue('rate_rules_export_period', val)"
                    />
                </validation-provider>
                <validation-provider
                    name="booking_lead_time"
                    rules="required"
                >
                    <form-select
                        label="Booking Lead Time"
                        :options="bookingLeadTimeOptions"
                        v-model="values.booking_lead_time"
                        :error="errors['booking_lead_time']"
                        @change="(val) => setFieldValue('booking_lead_time', val)"
                    />
                </validation-provider>
                <div v-if="values.is_eu_country">
                    <form-input
                        label="Merchant Name"
                        v-model="values.merchant_name"
                    />
                    <b-button
                        v-if="userHasStripeGateway"
                        variant="default"
                        class="mb-2"
                        @click="fetchMerchantName"
                    >
                        Fetch from Stripe
                    </b-button>
                </div>
                

              <validation-provider
                    name="use_guest_email"
                    rules="required:allowFalse"
                >
                    <b-row class="mt-1">
                        <b-col>
                            Guest Communications
                        </b-col>
                    </b-row>                    
                    <b-row class="mt-2">
                        <b-col>
                            <b-alert show variant="info">
                                <p>
                                    Selecting "Use VRBO App" has the benefit of all trigger messaging being posted
                                    to the VRBO app. The downside is that the VRBO app will not display HTML, URLs, or attachments.
                                </p>
                                <p>
                                    Selecting "Use Guest Email" will allow for full HTML, the display of URLs and attachments,
                                    however the message will not appear in the VRBO app.
                                </p>
                            </b-alert>
                        </b-col>
                    </b-row>                    
                    <form-select
                        label=""
                        :options="guestCommunicationsOptions"
                        v-model="values.use_guest_email"
                        :error="errors.use_guest_email"
                        @change="(val) => setFieldValue('use_guest_email', val)"
                    />
                </validation-provider>                

                <div class="accordion" role="tablist">
                    <b-card no-body class="mb-1">
                        <b-card-header
                            header-tag="header"
                            class="p-1"
                            role="tab"
                        >
                            <b-button
                                block
                                v-b-toggle.accordion-1
                                variant="primary"
                                >Custom House Rule 1 (max 50 chars)</b-button
                            >
                        </b-card-header>
                        <b-collapse
                            id="accordion-1"
                            accordion="my-accordion"
                            role="tabpanel"
                        >
                            <b-card-body>
                                <validation-provider
                                    name="vrbo_house_rules_1"
                                    rules="maxMessageLen:51,Rule"
                                >
                                    <form-input
                                        v-model="
                                            values.vrbo_house_rules
                                                .custom_house_rule_1
                                        "
                                        :error="errors.vrbo_house_rules_1"
                                        @input="(val) => setFieldValue('vrbo_house_rules.custom_house_rule_1', val)"
                                    />
                                </validation-provider>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                    <b-card no-body class="mb-1">
                        <b-card-header
                            header-tag="header"
                            class="p-1"
                            role="tab"
                        >
                            <b-button
                                block
                                v-b-toggle.accordion-2
                                variant="primary"
                                >Custom House Rule 2 (max 50 chars)</b-button
                            >
                        </b-card-header>
                        <b-collapse
                            id="accordion-2"
                            accordion="my-accordion"
                            role="tabpanel"
                        >
                            <b-card-body>
                                <validation-provider
                                    name="vrbo_house_rules_2"
                                    rules="maxMessageLen:51,Rule"
                                >
                                    <form-input
                                        v-model="
                                            values.vrbo_house_rules
                                                .custom_house_rule_2
                                        "
                                        :error="errors.vrbo_house_rules_2"
                                        @input="(val) => setFieldValue('vrbo_house_rules_2', val)"
                                    />
                                </validation-provider>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                    <b-card no-body class="mb-1">
                        <b-card-header
                            header-tag="header"
                            class="p-1"
                            role="tab"
                        >
                            <b-button
                                block
                                v-b-toggle.accordion-3
                                variant="primary"
                                >Custom House Rule 3 (max 50 chars)</b-button
                            >
                        </b-card-header>
                        <b-collapse
                            id="accordion-3"
                            accordion="my-accordion"
                            role="tabpanel"
                        >
                            <b-card-body>
                                <validation-provider
                                    name="vrbo_house_rules_3"
                                    rules="maxMessageLen:51,Rule"
                                >
                                    <form-input
                                        v-model="
                                            values.vrbo_house_rules
                                                .custom_house_rule_3
                                        "
                                        :error="errors.vrbo_house_rules_3"
                                        @input="(val) => setFieldValue('vrbo_house_rules_3', val)"
                                    />
                                </validation-provider>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </div>
        </modal>
    </validation-observer>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import API from '../../../api';
import { PARTNER_VRBO_SETTINGS_ENDPOINT, PARTNER_VRBO_MERCHANT_NAME_ENDPOINT } from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import Modal from '../../generic/modal/modal.vue';
import { mapGetters } from 'vuex';

export default {
    name: 'VRBOSettingsModalForm',
    components: {
        BusyModal,
        FormSelect,
        FormInput,
        Modal,
    },
    props: {
        modalId: String,
        partnerSettings: Object,
    },
    data() {
        return {
            loading: false,
            values: {
                vrbo_house_rules: {
                    custom_house_rule_1: '',
                    custom_house_rule_2: '',
                    custom_house_rule_3: '',
                },
                commission: 0,
                instant_booking: false,
                merchant_name: '',
                rate_rules_export_period: 0,
                use_guest_email: true,
                booking_lead_time: 0,
                is_eu_country: false,
            },
            rateRulesExportPeriodOptions: [
                { value: 6, text: '6 Months' },
                { value: 12, text: '1 Year' },
                { value: 18, text: '18 Months' },
                { value: 24, text: '2 Years' },
                { value: 36, text: '3 Years' },
            ],
            guestCommunicationsOptions: [
                { value: false, text: 'Use VRBO App' },
                { value: true, text: 'Use Guest Email' },
            ],
            bookingLeadTimeOptions: [
                { value: 0, text: '0 days' },
                { value: 1, text: '1 day' },
                { value: 2, text: '2 days' },
                { value: 3, text: '3 days' },
                { value: 4, text: '4 days' },
                { value: 5, text: '5 days' },
                { value: 6, text: '6 days' },
                { value: 7, text: '7 days' },
                { value: 8, text: '8 days' },
                { value: 9, text: '9 days' },
                { value: 10, text: '10 days' },
            ],
            show: true,
        };
    },
    computed: {
        ...mapGetters('header', ['userHasStripeGateway']),
    },
    methods: {
        getInitialValues() {
            return {
                vrbo_house_rules: {
                    custom_house_rule_1: this.partnerSettings.vrbo_house_rules.custom_house_rule_1,
                    custom_house_rule_2: this.partnerSettings.vrbo_house_rules.custom_house_rule_2,
                    custom_house_rule_3: this.partnerSettings.vrbo_house_rules.custom_house_rule_3,
                },
                commission: this.partnerSettings.commission,
                instant_booking: this.partnerSettings.instant_booking,
                merchant_name: this.partnerSettings.merchant_name,
                rate_rules_export_period: this.partnerSettings.rate_rules_export_period,
                use_guest_email: this.partnerSettings.use_guest_email,
                booking_lead_time: this.partnerSettings.booking_lead_time,
                is_eu_country: this.partnerSettings.is_eu_country,
            };
        },
        setValues() {
            this.values = this.getInitialValues();
            this.$refs.form.setValues({
                ...this.values,
                vrbo_house_rules_1: this.values.vrbo_house_rules.custom_house_rule_1,
                vrbo_house_rules_2: this.values.vrbo_house_rules.custom_house_rule_2,
                vrbo_house_rules_3: this.values.vrbo_house_rules.custom_house_rule_3,
            });
        },
        hideModal() {
            this.$bvModal.hide(this.modalId);
        },
        hiddenModal() {
            this.$emit('hidden');
        },
        async fetchMerchantName() {
            try {
                const { data } = await API.get(PARTNER_VRBO_MERCHANT_NAME_ENDPOINT());
                this.values.merchant_name = data.merchant_name;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch merchant name' });
            }
        },
        async save() {
            this.loading = true;

            try {
                const { data } = await API.patch(PARTNER_VRBO_SETTINGS_ENDPOINT(), JSON.stringify(this.values));
                this.values = data;
                this.$nextTick(() => {
                    this.hideModal();
                });
                this.$emit('updated', data);
            } catch (error) {
                const { generic, fields } = formatResponseError(error);
                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.form.setErrors(fields);
                }
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.recommended-text {
    font-weight: bold;
    color: #0d47a1;
}
.custom-tooltip {
    position: relative;
    display: inline-block;
    .tooltiptext {
        width: 170px;
        background-color: #fff;
        text-align: center;
        padding: 5px 0;
        border-radius: 6px;
        position: absolute;
        z-index: 1;
        top: 4px;
        left: 110%;
        border: 1px solid rgba(0, 0, 0, 0.2);
        box-shadow: 0 5px 10px rgb(0 0 0 / 20%);
        &::after {
            content: ' ';
            position: absolute;
            top: 50%;
            right: 100%; /* To the left of the tooltip */
            margin-top: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: transparent #0d47a1 transparent transparent;
        }
    }
}
</style>
